/** Auth type options for collection-level editor (no inherit). */
export const COLLECTION_AUTH_TYPES = [
  { value: 'noauth', label: 'No Auth' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'apikey', label: 'API Key' },
  { value: 'basic', label: 'Basic Auth' },
  { value: 'oauth2', label: 'OAuth 2.0' },
  { value: 'awssigv4', label: 'AWS Signature v4' },
];

/** Auth type options for request-level editor (includes inherit). */
export const REQUEST_AUTH_TYPES = [
  { value: 'inherit', label: 'Inherit from parent' },
  ...COLLECTION_AUTH_TYPES,
];

const EMPTY_APIKEY = { key: '', value: '', in: 'header' };
const EMPTY_BASIC = { username: '', password: '' };
const EMPTY_AWSSIGV4 = {
  accessKeyId: '',
  secretAccessKey: '',
  sessionToken: '',
  region: '',
  service: '',
};
const EMPTY_OAUTH2 = {
  grantType: 'manual',
  authUrl: '',
  tokenUrl: '',
  clientId: '',
  clientSecret: '',
  scope: '',
  redirectUri: '',
  accessToken: '',
  refreshToken: '',
  tokenType: 'Bearer',
  expiresAt: null,
};

/** Default auth for new requests (inherit from collection). */
export function defaultRequestAuth() {
  return {
    type: 'inherit',
    bearer: '',
    apikey: { ...EMPTY_APIKEY },
    basic: { ...EMPTY_BASIC },
    awssigv4: { ...EMPTY_AWSSIGV4 },
    oauth2: { ...EMPTY_OAUTH2 },
  };
}

/** Default auth for collections. */
export function defaultCollectionAuth() {
  return {
    type: 'noauth',
    bearer: '',
    apikey: { ...EMPTY_APIKEY },
    basic: { ...EMPTY_BASIC },
    awssigv4: { ...EMPTY_AWSSIGV4 },
    oauth2: { ...EMPTY_OAUTH2 },
  };
}

/** Normalize persisted auth objects into a consistent shape. */
export function normalizeAuth(auth, { forRequest = false } = {}) {
  const base = forRequest ? defaultRequestAuth() : defaultCollectionAuth();
  if (!auth || typeof auth !== 'object') {
    return base;
  }
  const type =
    auth.type ||
    (forRequest && Object.keys(auth).length === 0 ? 'inherit' : base.type);
  return {
    ...base,
    ...auth,
    type,
    apikey: { ...base.apikey, ...(auth.apikey || {}) },
    basic: { ...base.basic, ...(auth.basic || {}) },
    awssigv4: { ...base.awssigv4, ...(auth.awssigv4 || {}) },
    oauth2: { ...base.oauth2, ...(auth.oauth2 || {}) },
  };
}

/**
 * Resolve request auth against a folder chain and collection auth, and say
 * WHERE the winning block came from.
 *
 * Postman scopes auth at request → folder(s) → collection. A request that
 * inherits walks up the nearest-folder-first chain; the first folder that
 * declares a concrete auth (not `inherit`) wins. A folder that explicitly
 * sets `noauth` stops inheritance with no auth. If every folder inherits,
 * the collection auth applies.
 *
 * `from` is `'request'`, `'folder'` (with `folderIndex` into the chain passed
 * in), `'collection'`, or `'none'` when nothing up the chain sets any auth.
 * The app's send path and MCP's `request_read` / `request_diagnose` both read
 * this one function, so the two cannot disagree about inheritance.
 *
 * @param {object} requestAuth
 * @param {Array<object>} folderAuthChain Nearest folder first; each may be null.
 * @param {object} collectionAuth
 * @returns {{auth: object, from: string, folderIndex: number}}
 */
export function resolveEffectiveAuthSource(requestAuth, folderAuthChain = [], collectionAuth) {
  const request = normalizeAuth(requestAuth, { forRequest: true });
  if (request.type !== 'inherit') {
    return { auth: request, from: 'request', folderIndex: -1 };
  }
  const chain = folderAuthChain || [];
  for (let i = 0; i < chain.length; i += 1) {
    const folderAuth = chain[i];
    if (!folderAuth) continue;
    const folder = normalizeAuth(folderAuth, { forRequest: true });
    if (folder.type === 'inherit') continue; // folder inherits → keep walking up
    if (folder.type === 'noauth') {
      return { auth: { ...defaultCollectionAuth(), type: 'noauth' }, from: 'folder', folderIndex: i };
    }
    return { auth: folder, from: 'folder', folderIndex: i };
  }
  const collection = normalizeAuth(collectionAuth, { forRequest: false });
  if (collection.type && collection.type !== 'noauth') {
    return { auth: collection, from: 'collection', folderIndex: -1 };
  }
  return { auth: { ...defaultCollectionAuth(), type: 'noauth' }, from: 'none', folderIndex: -1 };
}

/**
 * Resolve request auth against a folder chain and collection auth.
 * See {@link resolveEffectiveAuthSource}; this returns only the block.
 * @returns {object} Effective auth with a concrete type (never inherit).
 */
export function resolveEffectiveAuthChain(requestAuth, folderAuthChain = [], collectionAuth) {
  return resolveEffectiveAuthSource(requestAuth, folderAuthChain, collectionAuth).auth;
}

/**
 * Resolve request auth against collection auth when type is inherit.
 * Thin wrapper over {@link resolveEffectiveAuthChain} with no folder scope.
 * @returns {object} Effective auth with a concrete type (never inherit).
 */
export function resolveEffectiveAuth(requestAuth, collectionAuth) {
  return resolveEffectiveAuthChain(requestAuth, [], collectionAuth);
}

function sub(value, substituteVariables) {
  if (!substituteVariables || value == null) return String(value ?? '');
  return substituteVariables(String(value));
}

/**
 * `base64(username:password)` over the UTF-8 bytes (RFC 7617 `charset=UTF-8`,
 * what curl and Postman send). `btoa` alone encodes Latin-1: a `{{clientSecret}}`
 * holding `é` went out as the wrong bytes, and one holding `ł`, `€` or any CJK
 * character threw InvalidCharacterError, so the request was never sent.
 */
export function encodeBasicCredentials(username, password) {
  const credentials = `${username ?? ''}:${password ?? ''}`;
  if (typeof TextEncoder === 'function' && typeof btoa === 'function') {
    let binary = '';
    for (const byte of new TextEncoder().encode(credentials)) binary += String.fromCharCode(byte);
    return btoa(binary);
  }
  return Buffer.from(credentials, 'utf8').toString('base64');
}

function appendQueryParam(url, key, value) {
  if (!key) return url;
  const encoded = `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  if (!url) return `?${encoded}`;
  if (url.includes('?')) {
    return url.endsWith('?') || url.endsWith('&') ? `${url}${encoded}` : `${url}&${encoded}`;
  }
  return `${url}?${encoded}`;
}

/**
 * Apply effective auth to outbound headers and optionally URL.
 * Returns a Promise so that async auth types (e.g. AWS SigV4) are supported.
 * @returns {Promise<{ headers: object, url: string }>}
 */
export async function applyRequestAuth({ auth, headers, url, substituteVariables, method, body }) {
  const effective = normalizeAuth(auth, { forRequest: false });
  const nextHeaders = { ...headers };
  let nextUrl = url || '';

  if (!effective.type || effective.type === 'noauth' || effective.type === 'inherit') {
    return { headers: nextHeaders, url: nextUrl };
  }

  switch (effective.type) {
    case 'bearer': {
      const token = sub(effective.bearer, substituteVariables).trim();
      if (token) {
        nextHeaders.Authorization = `Bearer ${token}`;
      }
      break;
    }
    case 'apikey': {
      const key = sub(effective.apikey?.key, substituteVariables).trim();
      const value = sub(effective.apikey?.value, substituteVariables);
      if (key) {
        if (effective.apikey?.in === 'query') {
          nextUrl = appendQueryParam(nextUrl, key, value);
        } else {
          nextHeaders[key] = value;
        }
      }
      break;
    }
    case 'basic': {
      const username = sub(effective.basic?.username, substituteVariables);
      const password = sub(effective.basic?.password, substituteVariables);
      if (username || password) {
        nextHeaders.Authorization = `Basic ${encodeBasicCredentials(username, password)}`;
      }
      break;
    }
    case 'oauth2': {
      const token = sub(effective.oauth2?.accessToken, substituteVariables).trim();
      const tokenType = sub(effective.oauth2?.tokenType, substituteVariables).trim() || 'Bearer';
      if (token) {
        nextHeaders.Authorization = `${tokenType} ${token}`;
      }
      break;
    }
    case 'awssigv4': {
      const accessKeyId = sub(effective.awssigv4?.accessKeyId, substituteVariables).trim();
      const secretAccessKey = sub(effective.awssigv4?.secretAccessKey, substituteVariables).trim();
      const sessionToken = sub(effective.awssigv4?.sessionToken, substituteVariables).trim();
      const region = sub(effective.awssigv4?.region, substituteVariables).trim() || 'us-east-1';
      const service = sub(effective.awssigv4?.service, substituteVariables).trim() || 'execute-api';

      if (accessKeyId && secretAccessKey && nextUrl) {
        try {
          const { signRequest } = await import('./awsSigV4.js');
          const sigHeaders = await signRequest({
            method: (method || 'GET').toUpperCase(),
            url: nextUrl,
            headers: nextHeaders,
            body: body || '',
            accessKeyId,
            secretAccessKey,
            sessionToken: sessionToken || undefined,
            region,
            service,
          });
          Object.assign(nextHeaders, sigHeaders);
        } catch (err) {
          console.error('[SigV4] Signing failed:', err);
        }
      }
      break;
    }
    default:
      break;
  }

  return { headers: nextHeaders, url: nextUrl };
}
